// src/pages/Community/components/ProfileCard.jsx
import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * Canonical reaction set used across the app
 * Add / remove / reorder reactions here.
 */
const REACTIONS = [
  { id: "like",       emoji: "👍", label: "Like" },
  { id: "love",       emoji: "❤️", label: "Love" },
  { id: "fire",       emoji: "🔥", label: "Hot" },
  { id: "applause",   emoji: "👏", label: "Applause" },
  { id: "celebrate",  emoji: "🎉", label: "Celebrate" },
  { id: "funny",      emoji: "😂", label: "Funny" },
  { id: "respect",    emoji: "🤝", label: "Respect" },
  { id: "100",        emoji: "💯", label: "100" },
  { id: "helpful",    emoji: "💡", label: "Helpful" },
  { id: "strategy",   emoji: "🎯", label: "Strategy" },
  { id: "epic",       emoji: "😎", label: "Epic Moment" },
  { id: "insightful", emoji: "🧠", label: "Insightful" },
  { id: "curious",    emoji: "❓", label: "Curious" },
  { id: "agree",      emoji: "✅", label: "Agree" },
  { id: "disagree",   emoji: "❌", label: "Disagree" },
  { id: "support",    emoji: "🤗", label: "Support" },
  { id: "bookmark",   emoji: "🔖", label: "Save" },
  { id: "bug",        emoji: "🐞", label: "Issue" },
  { id: "cool",       emoji: "🆒", label: "Cool" },
  { id: "wow",        emoji: "😮", label: "Wow" },
  { id: "music",      emoji: "🎶", label: "Music" },
];

export default function ProfileCard({
  profile = {},
  currentUser = null,
  allowExpand = false,
  compact = false,
  onReact = () => {},
  reactionCountsFromParent = null,
  reactions = null, // optional override reaction list
}) {
  // Basic identity
  const uid = profile?.uid;

  // Use passed-in reactions list if provided; otherwise canonical list
  const reactionList = Array.isArray(reactions) && reactions.length ? reactions : REACTIONS;

  // Aggregated reaction counts shown in UI (map: reactionId -> number)
  const [reactionCounts, setReactionCounts] = useState(
    reactionCountsFromParent ?? profile?.reactionCounts ?? {}
  );

  // The current viewer's selected reaction id (if any)
  const [myReaction, setMyReaction] = useState(null);

  /**
   * Sync reactionCounts when parent provides them or when profile doc includes them.
   * Parent-provided counts take precedence.
   */
  useEffect(() => {
    if (reactionCountsFromParent) {
      console.log("[ProfileCard] using reactionCountsFromParent for", uid, reactionCountsFromParent);
      setReactionCounts(reactionCountsFromParent);
      return;
    }
    console.log("[ProfileCard] using profile.reactionCounts for", uid, profile?.reactionCounts);
    setReactionCounts(profile?.reactionCounts ?? {});
  }, [reactionCountsFromParent, profile?.reactionCounts, uid]);

  /**
   * Aggregation listener:
   * If parent doesn't supply counts, listen to achievements where uid == profile.uid
   * and sum their reactionCounts maps so the profile shows totals across achievements.
   *
   * NOTE: We use onSnapshot to keep totals realtime. Reaction keys that don't exist
   * are filled with 0 for consistent UI rendering.
   */
  useEffect(() => {
    if (!uid) {
      console.log("[ProfileCard] no uid, skipping achievements aggregation");
      return undefined;
    }
    if (reactionCountsFromParent) {
      console.log("[ProfileCard] reactionCountsFromParent provided, skip aggregating achievements for", uid);
      return undefined;
    }

    const achCol = collection(db, "achievements");
    const q = query(achCol, where("uid", "==", uid));

    console.log("[ProfileCard] attaching achievements aggregation listener for uid:", uid);
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        try {
          console.log("[ProfileCard] achievements snapshot for", uid, "size:", snapshot.size);
          const aggregated = {};
          snapshot.forEach((docSnap) => {
            const id = docSnap.id;
            const data = docSnap.data() || {};
            console.log("[ProfileCard] achievement doc:", id, data);
            const rc = data.reactionCounts || {};
            Object.keys(rc).forEach((k) => {
              const raw = rc[k];
              const num = typeof raw === "number" ? raw : (parseInt(raw, 10) || 0);
              aggregated[k] = (aggregated[k] || 0) + num;
            });
          });

          // ensure every known reaction key exists (helps UI show 0 if desired)
          reactionList.forEach((r) => {
            if (aggregated[r.id] == null) aggregated[r.id] = 0;
          });

          console.log("[ProfileCard] aggregated reactionCounts for", uid, aggregated);
          setReactionCounts(aggregated);
        } catch (e) {
          console.error("[ProfileCard] error while aggregating achievements for", uid, e);
        }
      },
      (err) => {
        console.error("[ProfileCard] achievements aggregation listener error for", uid, err);
      }
    );

    return () => {
      try {
        unsub && unsub();
        console.log("[ProfileCard] detached achievements listener for", uid);
      } catch (e) {
        /* ignore cleanup errors */
      }
    };
  }, [uid, reactionCountsFromParent, reactionList]);

  /**
   * Enhanced aggregation useEffect:
   * - Also listens to single doc at achievements/{uid} and the query where(uid == profile.uid),
   * - Merges counts from both sources (useful when you have a doc-per-profile and additional achievement docs).
   *
   * This block was provided earlier and kept for richer coverage; it returns early if the parent provides counts.
   */
  useEffect(() => {
    if (!uid) {
      return undefined;
    }
    if (reactionCountsFromParent) {
      return undefined;
    }

    let mounted = true;
    const aggregated = {}; // local working map

    function mergeCounts(rcMap) {
      if (!rcMap) return;
      Object.keys(rcMap).forEach((k) => {
        const raw = rcMap[k];
        const num = typeof raw === "number" ? raw : (parseInt(raw, 10) || 0);
        aggregated[k] = (aggregated[k] || 0) + num;
      });
    }

    // 1) Listen to possible doc at achievements/{uid}
    const docRef = doc(db, "achievements", uid);
    const unsubDoc = onSnapshot(
      docRef,
      (snap) => {
        if (!mounted) return;
        try {
          if (!snap.exists()) {
            console.log("[ProfileCard] achievement doc by id not found:", `achievements/${uid}`);
          } else {
            const d = snap.data() || {};
            console.log("[ProfileCard] achievement doc by id:", snap.id, d);
          }
          // note: recompute triggered below by query snapshot handler
        } catch (e) {
          console.error("[ProfileCard] error reading achievement doc by id for", uid, e);
        }
      },
      (err) => {
        console.error("[ProfileCard] achievement doc snapshot error for", uid, err);
      }
    );

    // 2) Query where uid == profile.uid (covers multi-doc model)
    const achCol = collection(db, "achievements");
    const q = query(achCol, where("uid", "==", uid));
    const unsubQuery = onSnapshot(
      q,
      async (snapshot) => {
        if (!mounted) return;
        try {
          console.log("[ProfileCard] achievements query snapshot for", uid, "size:", snapshot.size);

          // Reset aggregated map each snapshot (recompute from scratch)
          Object.keys(aggregated).forEach((k) => delete aggregated[k]);

          // Attempt to include counts from the single doc-by-id if it exists (one-time get)
          try {
            const { getDoc } = await import("firebase/firestore");
            const docSnap = await getDoc(doc(db, "achievements", uid));
            if (docSnap.exists()) {
              const d = docSnap.data() || {};
              console.log("[ProfileCard] merging counts from achievement doc-by-id:", docSnap.id, d.reactionCounts);
              mergeCounts(d.reactionCounts || {});
            } else {
              console.log("[ProfileCard] no achievement doc-by-id to merge for", uid);
            }
          } catch (errDocRead) {
            console.error("[ProfileCard] error fetching achievement doc-by-id for merge:", errDocRead);
          }

          // Merge counts from query results
          snapshot.forEach((docSnap) => {
            const data = docSnap.data() || {};
            console.log("[ProfileCard] merging counts from query doc:", docSnap.id, data.reactionCounts);
            mergeCounts(data.reactionCounts || {});
          });

          // Ensure all known reaction keys exist with 0 if absent
          reactionList.forEach((r) => {
            if (aggregated[r.id] == null) aggregated[r.id] = 0;
          });

          console.log("[ProfileCard] final aggregated reactionCounts for", uid, aggregated);
          setReactionCounts({ ...aggregated });
        } catch (e) {
          console.error("[ProfileCard] error aggregating achievements for", uid, e);
        }
      },
      (err) => {
        console.error("[ProfileCard] achievements query listener error for", uid, err);
      }
    );

    return () => {
      mounted = false;
      try { unsubDoc && unsubDoc(); } catch (e) {}
      try { unsubQuery && unsubQuery(); } catch (e) {}
      console.log("[ProfileCard] detached achievements aggregation listeners for", uid);
    };
  }, [uid, reactionCountsFromParent, reactionList]);

  // Memoize the buttons list (simple mapping of reactionList)
  const buttons = useMemo(() => reactionList, [reactionList]);

  /**
   * handleReactClick
   * - Calls onReact passed from parent with (selected, existing)
   * - Parent should handle toggling and optimistic updates as needed
   */
  function handleReactClick(reactionIdOrEmoji) {
    try {
      console.log("[ProfileCard] handleReactClick:", {
        profileUid: uid,
        viewer: currentUser?.uid,
        selection: reactionIdOrEmoji,
        existing: myReaction,
      });
    } catch (e) {
      /* ignore logging errors */
    }
    onReact(reactionIdOrEmoji, myReaction);
  }

  // Helper to return a numeric count (defensive)
  function getCountForId(id) {
    return reactionCounts?.[id] ?? 0;
  }

  /* =========================
     Compact card UI (mini) — avatar left spanning two rows,
     top row: name + badges (left) and level/xp (right),
     bottom row: reaction icons (only show reactions with > 0 count)
     ========================= */
  if (compact) {
    return (
      <div
        className="profile-compact-mini"
        role="group"
        aria-label={`Public profile for ${profile?.displayName || "user"}`}
      >
        <img
          className="pc-mini-avatar"
          src={profile?.photoURL || "/images/avatar-placeholder.png"}
          alt={`${profile?.displayName || "User"} avatar`}
          width={56}
          height={56}
        />

        {/* Top row: name + inline badges (left) ; level·xp (right) */}
        <div className="pc-mini-top" aria-hidden={false}>
          <div className="pc-mini-left">
            <div className="pc-mini-name">{profile?.displayName || "Unknown"}</div>

            {/* inline badges directly after the name */}
            <div className="pc-mini-badges" aria-hidden={false}>
              {(profile?.badges || []).slice(0, 2).map((b, i) => (
                <span key={i} className="badge-pill mini">{b}</span>
              ))}
            </div>
          </div>

          <div className="pc-mini-meta">
            {profile?.level || "Rookie"} • {profile?.xp ?? 0} XP
          </div>
        </div>

        {/* Bottom row: left placeholder for symmetry + left-aligned actions */}
        <div className="pc-mini-row" aria-hidden={false}>
          <div style={{ minWidth: 8 }} />

          <div className="pc-mini-actions" aria-hidden={false}>
            {buttons
              .filter((r) => getCountForId(r.id) > 0) // hide reactions with 0 count
              .map((r) => (
                <button
                  key={r.id}
                  className={`react-btn mini ${myReaction === r.id ? "active" : ""}`}
                  onClick={() => handleReactClick(r.id)}
                  aria-pressed={myReaction === r.id}
                  aria-label={r.label}
                  title={r.label}
                >
                  <span className="emoji" aria-hidden="true">{r.emoji}</span>
                  <span className="count">{getCountForId(r.id)}</span>
                </button>
              ))}
          </div>
        </div>
      </div>
    );
  }

  /* =========================
     Full profile card UI
     - Shows name, level/xp, badges, achievement count
     - Reaction icons: only those with count > 0 are rendered
     ========================= */
  return (
    <div
      className="profile-compact-card"
      role="group"
      aria-label={`Public profile for ${profile?.displayName || "user"}`}
    >
      <div className="pc-left">
        <img
          className="pc-avatar"
          src={profile?.photoURL || "/images/avatar-placeholder.png"}
          alt={`${profile?.displayName || "User"} avatar`}
          width={64}
          height={64}
        />
      </div>

      <div className="pc-body">
        <div className="pc-top">
          <div className="pc-name">{profile?.displayName || "Unknown"}</div>
          <div className="pc-meta">
            {profile?.level || "Rookie"} • {profile?.xp ?? 0} XP
          </div>
        </div>

        <div className="pc-badges">
          {(profile?.badges || []).slice(0, 3).map((b, idx) => (
            <span key={idx} className="badge-pill">{b}</span>
          ))}
          {profile?.achievementsCount ? (
            <span className="muted">{profile.achievementsCount} achievements</span>
          ) : null}
        </div>

        <div className="pc-actions">
          {buttons
            .filter((r) => getCountForId(r.id) > 0) // only reactions with count > 0
            .map((r) => (
              <button
                key={r.id}
                className={`react-btn ${myReaction === r.id ? "active" : ""}`}
                onClick={() => handleReactClick(r.id)}
                aria-pressed={myReaction === r.id}
                title={r.label}
              >
                <span className="emoji">{r.emoji}</span>
                <span className="count">{getCountForId(r.id)}</span>
              </button>
            ))}
          {allowExpand && (
            <button
              className="view-ach"
              onClick={() => window.scrollTo({ top: window.scrollY + 1, behavior: "smooth" })}
            >
              View achievements
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

ProfileCard.propTypes = {
  profile: PropTypes.object,
  currentUser: PropTypes.object,
  allowExpand: PropTypes.bool,
  compact: PropTypes.bool,
  onReact: PropTypes.func,
  reactionCountsFromParent: PropTypes.object,
  reactions: PropTypes.array,
};
